
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import axios from 'axios'
import SimpleUploader from '@/components/media/SimpleUploader.vue'
import { Base64MediaFile, getBase64Async } from '@/types/media'
import PreviewContainer from '@/components/media/PreviewContainer.vue'
import { SystemMessageData } from '@/types/systemMessage'
import { parseInt, capitalize } from 'lodash'

@Component({
  components: { PreviewContainer, SimpleUploader }
})
export default class SystemMessageDetailsModal extends Vue {
  @Prop({ required: true }) triggerId!: number
  @Prop({ required: false }) messageId!: number|null
  @Prop({ default: false }) visible!: boolean
  @Prop({ default: () => [] }) categories!: string[]
  @Prop({ default: () => [] }) types!: string[]
  @Prop({ default: () => [] }) targets!: string[]
  @Prop({ default: () => [] }) related!: SystemMessageData[]

  formData : SystemMessageData = new SystemMessageData()
  previewFiles:Base64MediaFile[] = []
  file: File|null = null
  selectedTargets: string[] = []
  selectedType = 1
  currentTitle = ''
  currentId: number|null = null
  linkedIds: number[] = []
  linkedMessageIds: number[] = []
  selectedFileId: number|null|undefined = null
  capitalize = capitalize

  @Watch('visible')
  onVisibleChange (val: boolean): void {
    this.modalVisible = val
    if (val) {
      if (this.messageId) {
        this.currentId = this.messageId
        this.resetForm()
        this.getEventMessage()
      }
    }
    this.formData = new SystemMessageData()
  }

  modalVisible = false
  get isVisible (): boolean {
    return this.visible && this.modalVisible
  }

  getEventMessage (): void {
    axios.get(process.env.VUE_APP_API_URL + '/system-messages/' + this.messageId)
      .then((response) => {
        this.setMessageData(response.data)
      })
  }

  resetForm (): void {
    this.formData = new SystemMessageData()
    this.previewFiles = []
    this.selectedTargets = []
    this.selectedFileId = undefined
    this.linkedMessageIds = []
    this.currentTitle = ''
    this.selectedType = 1
  }

  closeModal (): void {
    this.modalVisible = false
    this.$emit('closed', false)
  }

  async handleMedia (file: File): Promise<void> {
    const previewFile: Base64MediaFile = new Base64MediaFile(file)
    previewFile.url = await getBase64Async(file)
    previewFile.name = file.name.replace(/\.[^/.]+$/, '')
    this.previewFiles = [previewFile]
    this.formData.preview = file
  }

  deleteFile (): void {
    this.formData.preview = null
    this.previewFiles = []
  }

  get filteredRelated (): SystemMessageData[] {
    return this.related.filter((msg) => msg.type === 0 && !this.linkedMessageIds.includes(msg.id) && msg.id !== this.currentId && !this.linkedIds.includes(msg.id))
  }

  get canSave (): boolean {
    if (this.isAutoManaged) {
      return true
    }

    return this.currentTitle !== '' &&
    this.formData.type >= 0 &&
    this.selectedTargets.length > 0 &&
    this.formData.preview !== null
  }

  get title (): string {
    return this.messageId ? 'Bewerk bericht' : 'Nieuw bericht'
  }

  get loaded (): boolean {
    return this.messageId ? this.formData.id !== null : true
  }

  get isAutoManaged (): boolean {
    return this.formData.auto_managed === true
  }

  getFormData (): FormData {
    const formData = new FormData()

    if (this.isAutoManaged) {
      formData.append('linked', this.linkedMessageIds.join(','))

      if (this.formData.preview) {
        formData.append('files[]', this.formData.preview)
      }

      return formData
    }

    formData.append('trigger_id', this.triggerId.toString())
    formData.append('type', this.selectedType.toString())
    formData.append('name', this.currentTitle)
    formData.append('linked', this.linkedMessageIds.join(','))
    formData.append('targets', this.selectedTargets.map((target:string) => {
      return isNaN(Number(target)) ? this.targets.indexOf(target).toString() : target
    }).join(','))

    if (this.formData.preview) {
      formData.append('files[]', this.formData.preview)
    }
    return formData
  }

  addSelectedLinkedMessage (): void {
    if (this.selectedFileId && !this.linkedMessageIds.includes(this.selectedFileId)) {
      this.linkedMessageIds.push(this.selectedFileId)
    }
    this.selectedFileId = undefined
  }

  removeLinkedMessage (messageId: number): void {
    this.linkedMessageIds = this.linkedMessageIds.filter((id) => id !== messageId)
  }

  openLinkedMessage (messageId: number): void {
    const message = this.related.find((msg) => msg.id === messageId)
    if (message) {
      this.setMessageData(message)
    }
  }

  setMessageData (message: SystemMessageData): void {
    this.formData = message
    this.currentId = message.id
    this.previewFiles = Array.isArray(message.preview) ? [...message.preview] : []
    this.selectedTargets = this.formData.targets.split(',').map((target:string) => this.targets[parseInt(target)])
    this.selectedType = this.formData.type
    this.currentTitle = this.formData.name
    this.linkedIds = message.linked.length > 0 ? message.linked.join(',').split(',').map((id:string) => parseInt(id)) : []
    this.linkedMessageIds = [...this.linkedIds]
  }

  save (): void {
    const formData = this.getFormData()
    const headers = {
      'Content-Type': 'multipart/form-data'
    }
    axios.post(process.env.VUE_APP_API_URL + '/system-messages', formData, { headers: headers })
      .then(() => {
        this.modalVisible = false
        this.$emit('closed', true)
      })
  }

  update (): void {
    const formData = this.getFormData()
    formData.append('_method', 'PUT')

    const headers = {
      'Content-Type': 'multipart/form-data'
    }
    let wasUpdated = false
    axios.post(process.env.VUE_APP_API_URL + '/system-messages/' + this.messageId, formData, { headers: headers })
      .then(() => {
        wasUpdated = true
      }).catch(() => {
        wasUpdated = false
      })
      .finally(() => {
        this.modalVisible = false
        this.$emit('closed', wasUpdated)
      })
  }
}
